import React, { useEffect, lazy, Suspense } from 'react'
import { Switch, Redirect, Route } from 'react-router-dom'
import PropTypes from 'prop-types'
import TagManager from 'react-gtm-module'
import { withStyles } from '@aura/components/styles'
import { useCookies } from 'react-cookie'
import { useTranslation } from 'react-i18next'
import { useAuraThemeSwitcher } from '@aura/components/themes'
import { CONTEXT_ROUTE, LOGIN_APP_ROUTE, LOGIN_APP_ROUTE_WITH_LANG, LOGOUT_ROUTE, REGISTRATION_ROUTE, FORGOTPWD_ROUTE, NO_ACCESS, SSO_ERROR, SESSION_TIMEOUT, PASSWORD_CHANGED, ANALYTICS_REGISTER, ANALYTICS_REGISTER_WITH_LANG, LOGOUT_SUCCESS, FAILED_POLICY, SECURITY_WARNING, APP_MAINTENANCE, APP_INACTIVE, HEDGEX_LANDING, HEDGEX_LANDING_WITH_LANG, MAGIC_LINK, DEFAULT_REGISTER, DEFAULT_REGISTER_WITH_LANG, AAG_LANDING, AAG_LANDING_WITH_LANG } from '../../constants/routes'
import ContentContainer from '../../components/content-container'
import AppContext, { getContext, getLoginUrl } from '../../utils/app-context'
import { useConfig } from '../../utils/config'
import { cookieNameLocale, cookieNameTheme, isFrench } from '../../constants/app'
import constants from '../../constants/languages'
import SUPPORTED_THEMES from '../../constants/theme'
import { hideLoader, updateLoaderTheme } from '../../utils/loader-actions'
import Notifications from '../../components/notifications'
import styles from './styles'


const RemoveLoaderOnMount = ({ children, delay = 0 }) => {
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            hideLoader()
        }, delay)

        return () => clearTimeout(timeoutId)
    }, [ delay ])

    return children
}


const App = props => {
    const config = useConfig()
    const [ cookies, , removeCookie ] = useCookies([ cookieNameLocale, cookieNameTheme ])
    const { i18n } = useTranslation()
    const { theme, updateTheme } = useAuraThemeSwitcher()
    const hostname = window.location.hostname !== 'localhost' ? '.' + window.location.hostname.match(/([a-z0-9-]*?)\.[a-z]{2,}$/)[1] + '.com' : 'localhost'

    useEffect(() => {
        if (config.googleTagManagerId) {
            TagManager.initialize({
                gtmId: config.googleTagManagerId
            })
        }
        if (cookies?.locale) {
            i18n.changeLanguage(isFrench(cookies.locale) ? constants.FRENCH : constants.ENGLISH)
            removeCookie(cookieNameLocale, { domain: hostname, path: '/' })
        }
        if (cookies?.theme && [ SUPPORTED_THEMES.DARK, SUPPORTED_THEMES.LIGHT ].includes(cookies.theme)) {
            if (theme.palette.mode !== cookies.theme) {
                updateTheme({ palette: cookies.theme })
            }
            removeCookie(cookieNameTheme, { domain: hostname, path: '/' })
        }
        document.body.classList.remove('loader-active')
    }, [])

    useEffect(() => {
        updateLoaderTheme(theme.palette.mode)
    }, [ theme ])

    const context = {
        activeSession: props?.activeForgotPasswordSession || props?.activeLoginSession || props?.activeRegistrationSession,
        application: getContext()
    }
    const registerLocale = isFrench(i18n.language) ? constants.FRENCH : constants.ENGLISH

    return (
        <AppContext.Provider value={context}>
            <ContentContainer>
                <Content registerLocale={registerLocale} {...props}/>
            </ContentContainer>
            <Notifications
                notifications={props.notifications}
                removeNotification={props.removeNotification}
            />
        </AppContext.Provider>
    )
}

App.displayName = 'App'

const Login = lazy(() => import('../../containers/login'))
const Magic = lazy(() => import('../../containers/magic-link'))
const Logout = lazy(() => import('../../components/logout'))
const Registration = lazy(() => import('../../containers/registration'))
const NoAccess = lazy(() => import('../no-access'))
const SsoError = lazy(() => import('../sso-error'))
const AnalyticsRegister = lazy(() => import('../../containers/analytics-register'))
const DefaultRegister = lazy(() => import('../../containers/default-register'))
const Forgotpwd = lazy(() => import('../../containers/forgotpwd'))
const SessionTimeout = lazy(() => import('../session-timeout-login'))
const StandardMessage = lazy(() => import('../../containers/standard-messages'))
const HedgeXLanding = lazy(() => import('../hedgex-landing'))
const AAGLanding = lazy(() => import('../aag-landing'))

const Content = React.memo(({ registerLocale }) => {
    return (
        <Suspense>
            <Switch>
                <Route path={HEDGEX_LANDING_WITH_LANG}><RemoveLoaderOnMount><HedgeXLanding /></RemoveLoaderOnMount></Route>
                <Route path={HEDGEX_LANDING}><RemoveLoaderOnMount><HedgeXLanding /></RemoveLoaderOnMount></Route>
                <Route path={AAG_LANDING_WITH_LANG}><RemoveLoaderOnMount><AAGLanding /></RemoveLoaderOnMount></Route>
                <Route path={AAG_LANDING}><RemoveLoaderOnMount><AAGLanding /></RemoveLoaderOnMount></Route>
                <Route path={LOGIN_APP_ROUTE_WITH_LANG}><Login /></Route>
                <Route path={MAGIC_LINK}><RemoveLoaderOnMount><Magic /></RemoveLoaderOnMount></Route>
                <Route path={LOGIN_APP_ROUTE}><Login /></Route>
                <Route exact path={REGISTRATION_ROUTE}><RemoveLoaderOnMount><Registration /></RemoveLoaderOnMount></Route>
                <Route exact path={FORGOTPWD_ROUTE}><RemoveLoaderOnMount><Forgotpwd /></RemoveLoaderOnMount></Route>
                <Route exact path={LOGOUT_ROUTE}><RemoveLoaderOnMount><Logout /></RemoveLoaderOnMount></Route>
                <Route exact path={NO_ACCESS}><RemoveLoaderOnMount><NoAccess /></RemoveLoaderOnMount></Route>
                <Route exact path={SSO_ERROR}><RemoveLoaderOnMount><SsoError /></RemoveLoaderOnMount></Route>
                <Route exact path={ANALYTICS_REGISTER_WITH_LANG}><RemoveLoaderOnMount><AnalyticsRegister /></RemoveLoaderOnMount></Route>
                <Route exact path={ANALYTICS_REGISTER}><RemoveLoaderOnMount><AnalyticsRegister /></RemoveLoaderOnMount></Route>
                <Route exact path={DEFAULT_REGISTER_WITH_LANG}><RemoveLoaderOnMount delay={300}><DefaultRegister /></RemoveLoaderOnMount></Route>
                <Route exact path={DEFAULT_REGISTER}><Redirect to={`${DEFAULT_REGISTER}/${registerLocale}`} /></Route>
                <Route exact path={SESSION_TIMEOUT}><RemoveLoaderOnMount><SessionTimeout /></RemoveLoaderOnMount></Route>
                <Route exact path={LOGOUT_SUCCESS}><StandardMessage /></Route>
                <Route exact path={PASSWORD_CHANGED}><RemoveLoaderOnMount><StandardMessage /></RemoveLoaderOnMount></Route>
                <Route exact path={FAILED_POLICY}><RemoveLoaderOnMount><StandardMessage /></RemoveLoaderOnMount></Route>
                <Route exact path={SECURITY_WARNING}><RemoveLoaderOnMount><StandardMessage /></RemoveLoaderOnMount></Route>
                <Route exact path={APP_MAINTENANCE}><RemoveLoaderOnMount><StandardMessage /></RemoveLoaderOnMount></Route>
                <Route exact path={APP_INACTIVE}><RemoveLoaderOnMount><StandardMessage /></RemoveLoaderOnMount></Route>
                <Route path={CONTEXT_ROUTE}><Redirect to={getLoginUrl(window.location)}/></Route>
            </Switch>
        </Suspense>
    )
})

Content.displayName = 'App.Content'

Content.defaultProps = {
    registerLocale: constants.ENGLISH
}

Content.propTypes = {
    registerLocale: PropTypes.string
}

export default withStyles(App, styles)
